import React from 'react';
import { motion } from 'framer-motion';
import Reveal from '../ui/Reveal';

const signals = [
  'Your team checks in with you before making decisions that should be theirs.',
  'You personally closed more than half your new clients in the last 3 months.',
  'Revenue has been flat for 6 months despite you working harder.',
  'Delegation keeps failing — work comes back to you every time.',
  'You cannot explain in one sentence who your offer is for and why they choose you.',
];

export default function Diagnostic() {
  const goToAudit = () => {
    window.open('/diagnostic', '_blank');
  };

  return (
    <section
      id="diagnostic"
      className="text-brand-cream"
      style={{ backgroundColor: '#2C1A10', paddingTop: 96, paddingBottom: 96 }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">

          {/* LEFT — Copy */}
          <div>
            <Reveal>
              <p className="text-xs font-semibold tracking-widest uppercase text-brand-terra mb-4">
                Free Founder Audit
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-cream leading-tight mb-6">
                Not sure where you're stuck?
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-brand-cream/60 text-base leading-relaxed mb-8" style={{ maxWidth: 460 }}>
                Most founders can feel the friction but cannot name it from inside the business. The Founder Dependency Audit asks 10 yes/no questions and gives you a named diagnosis — not a vague score — in under 3 minutes.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex flex-wrap gap-4 text-xs text-brand-cream/40 mb-10">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-sand flex-shrink-0" />
                  10 questions
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-sand flex-shrink-0" />
                  Instant result
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-sand flex-shrink-0" />
                  No email required
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-sand flex-shrink-0" />
                  Built for post-revenue founders
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <button
                onClick={goToAudit}
                className="inline-block bg-brand-terra text-brand-cream font-semibold text-sm px-8 py-4 rounded-sm tracking-wide hover:opacity-90 transition-opacity"
              >
                Take the free audit →
              </button>
            </Reveal>
          </div>

          {/* RIGHT — Signal preview card */}
          <Reveal delay={0.2}>
            <div
              className="rounded-lg overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}
            >
              <div
                className="px-6 py-4 flex items-center justify-between"
                style={{ borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-xs font-semibold tracking-widest uppercase text-brand-terra">
                  Dependency signals
                </span>
                <span className="text-xs text-brand-cream/30">Do any of these sound familiar?</span>
              </div>
              <div className="px-6 py-5 flex flex-col gap-0">
                {signals.map((signal, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                    className="flex items-start gap-3 py-4"
                    style={{ borderBottom: i < signals.length - 1 ? '0.5px solid rgba(255,255,255,0.06)' : 'none' }}
                  >
                    <span
                      className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                      style={{ background: '#C94830' }}
                    />
                    <p className="text-sm text-brand-cream/70 leading-relaxed">{signal}</p>
                  </motion.div>
                ))}
              </div>
              <div
                className="px-6 py-4"
                style={{ borderTop: '0.5px solid rgba(255,255,255,0.08)', background: 'rgba(201,72,48,0.06)' }}
              >
                <p className="text-xs text-brand-cream/40 leading-relaxed">
                  If two or more of these are true, the audit will tell you exactly which constraint is running your business.
                </p>
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
}